<script lang="ts">
	// Stores
	import { sidePanelState, sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';

	// Design System Components
	import { SidepanelLayout, SidepanelTabs } from '@Shelly/shelly-design-system';

	// Helpers
	import { checkIsComponent } from 'src/components/Sidepanel/sidepanel-helpers';

	// Components
	import Main from './Tabs/Main.svelte';
	import SecondaryControls from './Tabs/SecondaryControlsGen2.svelte';
	import Notifications from './Tabs/Notifications.svelte';
	import Statistics from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/CommonDeviceTabs/Statistics.svelte';
	import ActivityLog from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/CommonDeviceTabs/ActivityLog.svelte';

	// TabsManager imports
	import SidePanelSettingsModalProvider from 'src/components/Sidepanel/SidePanelSettingsModal/SidePanelSettingsModalProvider.svelte';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { getDeviceChartsConfig } from 'src/components/Chart/chart_config';
	import { TabId, type BaseTab } from 'src/components/Sidepanel/sidepanel-types';
	import { createTabsManager } from 'src/components/Sidepanel/createTabsManager';
	import { setSidepanelTabNavigationContext } from 'src/components/Sidepanel/sidepanel-tab-navigation-context';
	import { device_notifications } from 'src/stores/features/device-notifications';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';

	// Helpers for secondary controls logic
	import { devices_config } from 'src/configs/devices';
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import {
		get_mode,
		real_id,
		isRgbwwPm,
		isCctOnlyDevice,
		isXMODDevice,
	} from 'src/helpers/interface_helpers';

	let showSettingsModal = false;

	$: if ($sidePanelState.showSettingsModal === true && !showSettingsModal) {
		showSettingsModal = true;
	}

	$: id = $sidePanelStateCombined?.id;
	$: device = $devices[id];
	$: deviceName = device?.name || 'SNDC-0D4P10WW';
	$: deviceRoom = $rooms?.[device?.room_id]?.name;
	$: deviceState = getDeviceState(id);
	$: hasStatisticsContent = device ? getDeviceChartsConfig(device).length > 0 : false;

	// Determine if secondary controls should be shown (logic from DeviceDrawer)
	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: mode = get_mode(device, status, settings);
	$: deviceConfig = devices_config?.[device?.type];

	$: hasColorTempControlForTabs = isXMODDevice(device?.type)
		? status?.['cct:0'] && device?.mode === 'cct'
		: deviceConfig?.modes?.[mode]?.color_temp;
	$: hasColorControlForTabs = isXMODDevice(device?.type)
		? status?.['rgbw:0'] && device?.mode === 'rgbw'
		: deviceConfig?.modes?.[mode]?.color;

	$: shouldShowSecondaryControls =
		(!isRgbwwPm(device?.type) &&
			device?.category === 'light' &&
			(hasColorTempControlForTabs || hasColorControlForTabs)) ||
		isCctOnlyDevice(device?.type) ||
		device?.mode === 'rgb' ||
		device?.mode === 'rgbw' ||
		device?.mode === 'cct';
	$: isExtractedTempSensor = Number(device?.channel) >= 100;

	// Build baseTabs conditionally
	let baseTabs: BaseTab[];

	$: baseTabs = [
		{
			id: TabId.MAIN,
			icon: 'fat fa-sliders-simple',
			component: Main,
		},
		shouldShowSecondaryControls && {
			id: TabId.SECONDARY_CONTROLS,
			icon: 'fat fa-palette',
			component: SecondaryControls,
		},
		...(hasStatisticsContent
			? [{ id: TabId.STATISTICS, icon: 'fat fa-chart-line', component: Statistics }]
			: []),
		...(!isExtractedTempSensor
			? [{ id: TabId.ACTIVITY_LOG, icon: 'fat fa-notebook', component: ActivityLog }]
			: []),
		{
			id: TabId.NOTIFICATIONS,
			icon: 'fat fa-bell',
			component: Notifications,
		},
	].filter(Boolean);

	$: tabsManager = createTabsManager(baseTabs);
	$: tabsState = tabsManager?.getTabsState();

	const handleSettings = () => {
		showSettingsModal = true;
	};

	$: hasCriticalNotifs =
		$device_notifications && id
			? getCriticalNotifications(getNotificationsForDevice(id)).length > 0
			: false;
	$: tabs = tabsManager?.getTabsConfig($tabsState?.tabIndex, device, $deviceState)?.map((tab) => ({
		...tab,
		...(tab.id === TabId.NOTIFICATIONS && hasCriticalNotifs
			? { notification: 'danger' as const, icon: 'fat fa-bell-on' }
			: {}),
	}));

	$: if (tabsManager && tabsState) {
		setSidepanelTabNavigationContext({
			navigateToTab: tabsManager.navigateToTabById,
			getTabIndex: tabsManager.getTabIndexById,
			openedEventTimestamp: $tabsState.openedEventTimestamp,
			activeHistoryButton: $tabsState.activeHistoryButton,
		});
	}

	$: if (!$sidePanelState.open && tabsManager) {
		tabsManager.resetActiveTab();
	}
</script>

<SidepanelLayout title={deviceName} subtitle={deviceRoom} onSettings={handleSettings}>
	<svelte:component
		this={checkIsComponent(tabs?.[$tabsState?.tabIndex]?.component)}
		{device}
		{...tabs?.[$tabsState?.tabIndex]?.component === ActivityLog &&
		$tabsState?.openedEventTimestamp !== null
			? { openedEventTimestamp: $tabsState?.openedEventTimestamp }
			: {}}
	/>
	<SidepanelTabs slot="tabs" {tabs} on:click={tabsManager?.onTabClick} />
</SidepanelLayout>

{#if showSettingsModal && device}
	<SidePanelSettingsModalProvider bind:showSettingsModal {device} />
{/if}
